/**
 * Purpose: Dynamic tier selector that shows age-group-appropriate labels.
 * Owns: Tier chip buttons with labels that change based on age bracket.
 * Notes: Internal tier values (student/scholar/graduate) never change — only
 *        display labels swap. Ages 3-11 see Smartie/Awesome/Super Star,
 *        ages 12-College see Student/Scholar/Graduate, Parents see
 *        Street Smart/Book Smart/Real Smart. Driven by modeConfig (CS-21).
 */

import type { Tier, AgeGroup } from '../../types/question';
import { getTierOptionsForAgeGroup } from '../../config/modeConfig';

interface TierSelectorProps {
  /** Player's selected age group — determines which tier labels to show */
  ageGroup: AgeGroup;
  /** Currently selected tier value */
  selectedTier: Tier;
  /** Called when the user taps a tier option */
  onTierChange: (tier: Tier) => void;
}

export function TierSelector({
  ageGroup,
  selectedTier,
  onTierChange,
}: TierSelectorProps) {
  const options = getTierOptionsForAgeGroup(ageGroup);

  return (
    <div style={styles.group}>
      <label style={styles.label}>Difficulty</label>
      <div style={styles.chips}>
        {options.map((opt) => (
          <button
            key={opt.value}
            style={{
              ...styles.chip,
              ...(selectedTier === opt.value ? styles.chipActive : {}),
            }}
            onClick={() => onTierChange(opt.value)}
            aria-pressed={selectedTier === opt.value}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  group: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.4rem',
  },
  label: {
    fontSize: '0.8rem',
    fontWeight: '600',
    color: 'var(--muted)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.4rem',
  },
  chip: {
    padding: '0.5rem 0.75rem',
    borderRadius: '8px',
    backgroundColor: 'var(--bg-surface)',
    color: 'var(--text-light)',
    fontSize: '0.85rem',
    fontWeight: '500',
    border: '1px solid transparent',
    cursor: 'pointer',
  },
  chipActive: {
    backgroundColor: 'var(--accent-wood)',
    border: '1px solid var(--accent-gold)',
    color: 'var(--text-light)',
  },
};
